import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
	useMemo,
} from "react";
import { usePostHog } from "posthog-js/react";
import {
	TabConfig,
	GeneralTabConfig,
	DensitySettings,
} from "@/dentlab/src/types/tab-config.types";
import {
	DEFAULT_DENSITY,
	normalizeGeneralTab,
} from "@/dentlab/src/config/density-config";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { cn } from "@/dentlab/src/lib/util";
import { TabPanelCard } from "@/dentlab/src/components/tab-panel-card/tab-panel-card.component";
import { renderIcon } from "@/dentlab/src/pages/job-configuration/utils/icon-mapper";
import { FieldRenderer } from "./field-renderer.component";
import { getTextColorForBackground } from "@/dentlab/src/components/utils/color.utils";
import { useAutoSave } from "@/dentlab/src/hooks/useAutosave/useAutosave";
import { PatientJobs } from "@/dentlab/src/store/Sheets/sheet-contents/patient-sheet/components/PatientJobs";
import { JobNotesPanel } from "./job-notes/job-notes-panel.component";
import { JobFilesTab } from "./job-files-tab.component";
import { FileTabActions } from "./file-tab-actions.component";
import { FIELD_REGISTRY } from "@/dentlab/src/pages/job-configuration/field-registry";
import { JobInfoTabsPanel } from "./job-info-tabs-panel.component";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";
import { DeliveriesTab } from "./deliveries/deliveries-tab.component";
import { CreateDeliveryButton } from "./deliveries/create-delivery-button.component";
import { TotalTab } from "./total-tab.component";
import { PatientDivergenceDialog } from "./patient-divergence-dialog.component";
import { InProgressJobsSuggestionDialog } from "./in-progress-jobs-suggestion-dialog.component";

const TAB_WIDTH_PER_COL = { min: 150, max: 200 };

const AD_HOC_TAB_ID = "ad-hoc-missing-fields";

const FILE_THUMB = 96;
const FILE_GAP = 6;
const FILE_PAD = 18;
const fileSnapWidth = (n: number) =>
	FILE_PAD + n * FILE_THUMB + (n - 1) * FILE_GAP;
const FILE_SNAP_POINTS = Array.from({ length: 6 }, (_, i) =>
	fileSnapWidth(i + 1)
);
const snapToFiles = (w: number) =>
	FILE_SNAP_POINTS.reduce((best, pt) =>
		Math.abs(pt - w) < Math.abs(best - w) ? pt : best
	);

interface ConfigurableJobInfoTabsProps {
	isFullscreen?: boolean;
	onToggleFullscreen?: () => void;
	tabConfig: TabConfig[];
	densitySettings?: DensitySettings;
	/** Show flagged fields that aren't in the layout in a "Fehlende Angaben" tab. */
	showAdHocTab?: boolean;
}

export const ConfigurableJobInfoTabs: React.FC<
	ConfigurableJobInfoTabsProps
> = ({
	isFullscreen = false,
	onToggleFullscreen,
	tabConfig,
	densitySettings = DEFAULT_DENSITY,
	showAdHocTab = true,
}) => {
	const postHog = usePostHog();
	const { job, jobNotes, deliveries } = useJobStore((state) => ({
		job: state.job,
		jobNotes: state.jobNotes,
		deliveries: state.deliveries,
	}));

	const { practitionersLookup } = useCentralStore((state) => ({
		practitionersLookup: state.practitionersLookup,
	}));

	const { patientsLookup, getPatient } = usePatientStore((state) => ({
		patientsLookup: state.patientsLookup,
		getPatient: state.getPatient,
	}));

	const focusFieldId = useJobStore((state) => state.focusFieldId);
	const requestFieldFocus = useJobStore((state) => state.requestFieldFocus);
	const adHocFieldIds = useJobStore((state) => state.adHocFieldIds);
	const dismissAdHocFields = useJobStore((state) => state.dismissAdHocFields);

	const [openTabOrder, setOpenTabOrder] = useState<string[]>(() => {
		return tabConfig
			.filter((tab) => tab.isDefaultActive)
			.sort((a, b) => a.order - b.order)
			.map((tab) => tab.id);
	});

	const [expandedFilesTabId, setExpandedFilesTabId] = useState<string | null>(
		null
	);
	const [expandedPreviewSize, setExpandedPreviewSize] = useState<
		"small" | "medium" | "large"
	>("medium");

	const [panelCollapsed, setPanelCollapsed] = useState(() =>
		getLocalStorage("job-info-tabs-panel-collapsed", false)
	);
	const [tabWidths, setTabWidths] = useState<Record<string, number>>(() =>
		getLocalStorage("job-info-tab-widths", {})
	);

	const [resizingTabId, setResizingTabId] = useState<string | null>(null);
	const preFullscreenStateRef = useRef<{
		openTabOrder: string[];
		panelCollapsed: boolean;
	} | null>(null);

	const {
		formFieldsJob,
		formFieldsPatient,
		formFieldsPractitioner,
		saveJobFields,
		savePatientFields,
		savePractitionerFields,
		formErrors,
	} = useJobStore((state) => ({
		formFieldsJob: state.formFieldsJob,
		formFieldsPatient: state.formFieldsPatient,
		formFieldsPractitioner: state.formFieldsPractitioner,
		saveJobFields: state.saveJobFields,
		savePatientFields: state.savePatientFields,
		savePractitionerFields: state.savePractitionerFields,
		formErrors: state.formErrors,
	}));

	// Autosave for job fields
	useAutoSave({
		data: formFieldsJob,
		onSave: saveJobFields,
		shouldSave:
			Object.keys(formFieldsJob).length > 0 &&
			!Object.keys(formFieldsJob).some((field) => formErrors.jobs[field]),
		delay: 700,
	});

	// Autosave for patient fields
	useAutoSave({
		data: formFieldsPatient,
		onSave: savePatientFields,
		shouldSave:
			Object.keys(formFieldsPatient).length > 0 &&
			!!job?.patient_id &&
			!Object.keys(formFieldsPatient).some(
				(field) => formErrors.patients[field]
			),
		delay: 700,
	});

	// Autosave for practitioner fields
	useAutoSave({
		data: formFieldsPractitioner,
		onSave: savePractitionerFields,
		shouldSave:
			Object.keys(formFieldsPractitioner).length > 0 &&
			!!job?.practitioner_id &&
			!Object.keys(formFieldsPractitioner).some(
				(field) => formErrors.practitioners[field]
			),
		delay: 700,
	});

	useEffect(() => {
		if (job?.patient_id && !patientsLookup[job.patient_id]) {
			getPatient(job.patient_id);
		}
	}, [job?.patient_id, getPatient, patientsLookup]);

	// Get current patient and practitioner for field rendering
	const currentPatient = job?.patient_id
		? patientsLookup[job.patient_id]
		: null;
	const currentPractitioner = job?.practitioner_id
		? practitionersLookup[job.practitioner_id]
		: null;

	// Helper to extract field value from data objects
	const getFieldValue = useCallback(
		(fieldId: string): string => {
			if (
				fieldId === "patient_select" ||
				fieldId === "practitioner_select"
			) {
				return ""; // Special fields don't have values for title
			}

			const [table, column] = fieldId.split(".");

			switch (table) {
				case "jobs":
					const jobValue = job?.[column as keyof typeof job];
					return jobValue ? String(jobValue) : "";
				case "patients":
					if (!currentPatient) return "";
					const patientValue = (
						currentPatient as Record<string, unknown>
					)?.[column];
					return patientValue ? String(patientValue) : "";
				case "practitioners":
					if (!currentPractitioner) return "";
					const practValue = (
						currentPractitioner as Record<string, unknown>
					)?.[column];
					return practValue ? String(practValue) : "";
				default:
					return "";
			}
		},
		[job, currentPatient, currentPractitioner]
	);

	const getTabLabel = useCallback(
		(tab: TabConfig): string => {
			switch (tab.type) {
				case "general":
					const generalTab = tab as GeneralTabConfig;

					// Check for fields that should be shown in title
					const titleFields =
						generalTab.fields?.filter((f) => f.useInTitle) || [];

					if (titleFields.length > 0) {
						const values = titleFields
							.map((field) => getFieldValue(field.fieldId))
							.filter((v) => v && v.trim() !== "");

						if (values.length > 0) {
							return values.join(", ");
						}
					}

					return generalTab.label || "Tab";

				case "notes":
					return jobNotes.length === 0
						? "+ Notiz"
						: `Notizen (${jobNotes.length})`;
				case "files":
					return "Dateien";
				case "job_history":
					return "Auftragshistorie";
				case "deliveries":
					return "Lieferungen";
				case "total":
					return "Total";
				default:
					return "Tab";
			}
		},
		[getFieldValue, jobNotes.length, deliveries]
	);

	// Helper function to check if a tab has incomplete required fields and get their names
	const tabHasIncompleteRequiredFields = useCallback(
		(
			tab: TabConfig
		): { hasIncomplete: boolean; missingFields: string[] } => {
			if (tab.type !== "general")
				return { hasIncomplete: false, missingFields: [] };

			const generalTab = tab as GeneralTabConfig;
			const requiredFields =
				generalTab.fields?.filter((f) => f.required) || [];
			const missingFields: string[] = [];

			for (const field of requiredFields) {
				const [table, column] = field.fieldId.split(".");
				let value: unknown;

				switch (table) {
					case "jobs":
						// Check both job data and form fields for the most current value
						value =
							formFieldsJob[
								column as keyof typeof formFieldsJob
							] !== undefined
								? formFieldsJob[
										column as keyof typeof formFieldsJob
									]
								: job?.[column as keyof typeof job];
						break;
					case "patients":
						if (!job?.patient_id) {
							value = null; // No patient selected
						} else {
							value =
								formFieldsPatient[
									column as keyof typeof formFieldsPatient
								] !== undefined
									? formFieldsPatient[
											column as keyof typeof formFieldsPatient
										]
									: currentPatient?.[
											column as keyof typeof currentPatient
										];
						}
						break;
					case "practitioners":
						if (!job?.practitioner_id) {
							value = null; // No practitioner selected
						} else {
							value =
								formFieldsPractitioner[
									column as keyof typeof formFieldsPractitioner
								] !== undefined
									? formFieldsPractitioner[
											column as keyof typeof formFieldsPractitioner
										]
									: currentPractitioner?.[
											column as keyof typeof currentPractitioner
										];
						}
						break;
					default:
						// Handle special fields
						if (field.fieldId === "patient_select") {
							value = job?.patient_id;
						} else if (field.fieldId === "practitioner_select") {
							value = job?.practitioner_id;
						}
						break;
				}

				// Check if the value is empty/null/undefined
				if (
					value === null ||
					value === undefined ||
					value === "" ||
					value === 0
				) {
					// Get the field label from the registry
					const fieldLabel =
						FIELD_REGISTRY[field.fieldId]?.label || field.fieldId;
					missingFields.push(fieldLabel);
				}
			}

			return { hasIncomplete: missingFields.length > 0, missingFields };
		},
		[
			formFieldsJob,
			formFieldsPatient,
			formFieldsPractitioner,
			job,
			currentPatient,
			currentPractitioner,
		]
	);

	const openTab = useCallback((tabId: string) => {
		setOpenTabOrder((prev) => {
			if (prev.includes(tabId)) return prev;
			return [tabId, ...prev];
		});
	}, []);

	const closeTab = useCallback(
		(tabId: string) => {
			if (tabId === AD_HOC_TAB_ID) {
				dismissAdHocFields();
				return;
			}
			setOpenTabOrder((prev) => prev.filter((id) => id !== tabId));
		},
		[dismissAdHocFields]
	);

	const togglePanelCollapsed = useCallback(() => {
		setPanelCollapsed((prev) => {
			const newValue = !prev;
			setLocalStorage("job-info-tabs-panel-collapsed", newValue);
			return newValue;
		});
	}, []);

	const handleToggleFullscreen = useCallback(() => {
		if (!onToggleFullscreen) return;

		if (!isFullscreen) {
			postHog.capture("open_all_tabs");
			preFullscreenStateRef.current = {
				openTabOrder: [...openTabOrder],
				panelCollapsed,
			};
			setOpenTabOrder(tabConfig.map((tab) => tab.id));
			setPanelCollapsed(true);
			setLocalStorage("job-info-tabs-panel-collapsed", true);
		} else {
			if (preFullscreenStateRef.current) {
				setOpenTabOrder(preFullscreenStateRef.current.openTabOrder);
				setPanelCollapsed(preFullscreenStateRef.current.panelCollapsed);
				setLocalStorage(
					"job-info-tabs-panel-collapsed",
					preFullscreenStateRef.current.panelCollapsed
				);
				preFullscreenStateRef.current = null;
			}
		}

		onToggleFullscreen();
	}, [
		isFullscreen,
		onToggleFullscreen,
		openTabOrder,
		panelCollapsed,
		tabConfig,
		postHog,
	]);

	const getTabCols = (tab: TabConfig): number =>
		tab.type === "general" ? (tab as GeneralTabConfig).columns ?? 1 : 1;

	const fontScale = Math.pow(densitySettings.fontSize / 12, 1.3);

	const TAB_TYPE_BOUNDS: Record<string, { min: number; max: number }> = {
		notes: { min: Math.round(200 * fontScale), max: 800 },
		job_history: { min: Math.round(240 * fontScale), max: 800 },
		deliveries: { min: Math.round(230 * fontScale), max: 800 },
		total: {
			min: Math.round(220 * fontScale),
			max: Math.round(240 * fontScale),
		},
	};

	const getTabBounds = (tab: TabConfig): { min: number; max: number } => {
		if (tab.type === "files")
			return {
				min: FILE_SNAP_POINTS[0],
				max: FILE_SNAP_POINTS[FILE_SNAP_POINTS.length - 1],
			};
		const fixed = TAB_TYPE_BOUNDS[tab.type];
		if (fixed) return fixed;
		const cols = getTabCols(tab);
		return {
			min: Math.round(cols * TAB_WIDTH_PER_COL.min * fontScale),
			max: Math.round(600 * fontScale),
		};
	};

	const getTabWidth = useCallback(
		(tab: TabConfig): number => {
			const { min, max } = getTabBounds(tab);
			const saved = tabWidths[tab.id];
			if (tab.type === "files") {
				return saved
					? snapToFiles(Math.min(Math.max(saved, min), max))
					: FILE_SNAP_POINTS[0];
			}
			if (saved) return Math.min(Math.max(saved, min), max);
			return min;
		},
		[tabWidths]
	);

	const handleToggleFilesExpand = useCallback((tabId: string) => {
		setExpandedFilesTabId((prev) => (prev === tabId ? null : tabId));
	}, []);

	const handleResizeStart = useCallback(
		(tabId: string, e: React.MouseEvent) => {
			e.preventDefault();
			setResizingTabId(tabId);
		},
		[]
	);

	useEffect(() => {
		if (!resizingTabId) return;

		const tab = tabConfig.find((t) => t.id === resizingTabId);
		if (!tab) return;

		const { min: minW, max: maxW } = getTabBounds(tab);

		const handleMouseMove = (e: MouseEvent) => {
			const tabElement = document.querySelector(
				`[data-tab-panel-id="${resizingTabId}"]`
			);
			if (!tabElement) return;

			const rect = tabElement.getBoundingClientRect();
			const newWidth = e.clientX - rect.left;
			const clamped = Math.min(Math.max(newWidth, minW), maxW);
			const clampedWidth =
				tab.type === "files" ? snapToFiles(clamped) : clamped;

			setTabWidths((prev) => {
				const newWidths = { ...prev, [tab.id]: clampedWidth };
				setLocalStorage("job-info-tab-widths", newWidths);
				return newWidths;
			});
		};

		const handleMouseUp = () => {
			setResizingTabId(null);
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);

		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};
	}, [resizingTabId, tabConfig]);

	// Flagged fields the user's layout doesn't place anywhere. Rendered in front of the
	// configured tabs and always open, so the focus effect can reach them.
	const adHocTab = useMemo((): GeneralTabConfig | null => {
		if (!showAdHocTab) return null;
		const fieldIdsInLayout = new Set(
			tabConfig.flatMap((tab) =>
				tab.type === "general"
					? (tab as GeneralTabConfig).fields.map(
							(field) => field.fieldId
						)
					: []
			)
		);
		const fieldIds = adHocFieldIds.filter(
			(fieldId) => !fieldIdsInLayout.has(fieldId)
		);
		if (fieldIds.length === 0) return null;
		return {
			id: AD_HOC_TAB_ID,
			type: "general",
			label: "Fehlende Angaben",
			order: -1,
			isDefaultActive: true,
			// Two columns so the default width shows the title instead of the compact header.
			columns: 2,
			fields: fieldIds.map((fieldId) => ({
				fieldId,
				required: false,
				colSpan: 2,
			})),
		};
	}, [showAdHocTab, tabConfig, adHocFieldIds]);

	const openTabs = useMemo(() => {
		const configuredTabs = openTabOrder
			.map((id) => tabConfig.find((t) => t.id === id))
			.filter((tab): tab is TabConfig => tab !== undefined);
		return adHocTab ? [adHocTab, ...configuredTabs] : configuredTabs;
	}, [openTabOrder, tabConfig, adHocTab]);

	const closedTabs = useMemo(() => {
		const openTabIds = new Set(openTabOrder);
		return tabConfig.filter((tab) => !openTabIds.has(tab.id));
	}, [tabConfig, openTabOrder]);

	// Focus is requested from outside this component (an export or item check that
	// flagged fields), and flagged fields may sit on closed tabs — so every tab holding
	// one is opened first and the focus happens on the following run, once the field
	// exists in the DOM.
	useEffect(() => {
		if (!focusFieldId) return;

		const flaggedFieldIds = new Set([
			focusFieldId,
			...Object.keys(useJobStore.getState().fieldProblems),
		]);
		const closedTabIds = tabConfig
			.filter(
				(tab) =>
					tab.type === "general" &&
					!openTabOrder.includes(tab.id) &&
					(tab as GeneralTabConfig).fields?.some((field) =>
						flaggedFieldIds.has(field.fieldId)
					)
			)
			.map((tab) => tab.id);

		if (closedTabIds.length > 0) {
			setOpenTabOrder((order) => [...order, ...closedTabIds]);
			return;
		}

		// Selects and comboboxes are focusable too — matching only input/textarea would
		// silently do nothing for them.
		const cell = document.querySelector<HTMLElement>(
			`[data-field-id="${focusFieldId}"]`
		);
		const target = cell?.querySelector<HTMLElement>(
			'input, textarea, [role="combobox"], button'
		);
		target?.focus();
		target?.scrollIntoView({ block: "nearest" });
		requestFieldFocus(null);
	}, [focusFieldId, openTabOrder, tabConfig, requestFieldFocus]);

	// Render tab content based on type
	const renderTabContent = useCallback(
		(tab: TabConfig) => {
			switch (tab.type) {
				case "general": {
					const normalized = normalizeGeneralTab(
						tab as GeneralTabConfig
					);
					const ds = densitySettings;
					const columns = normalized.columns ?? 1;

					const fieldMap = new Map(
						normalized.fields.map((f) => [
							`${f.col ?? 0},${f.row ?? 0}`,
							f,
						])
					);
					const rows = normalized.rows ?? normalized.fields.length;
					const gridElements: React.ReactNode[] = [];

					for (let r = 0; r < rows; r++) {
						for (let c = 0; c < columns; c++) {
							const field = fieldMap.get(`${c},${r}`);

							// Skip cells covered by a spanning field
							if (!field) {
								const coveredBy = normalized.fields.find(
									(f) => {
										const fc = f.col ?? 0;
										const fr = f.row ?? 0;
										return (
											fr === r &&
											fc < c &&
											fc + (f.colSpan ?? 1) > c
										);
									}
								);
								if (coveredBy) continue;
							}

							if (!field) {
								gridElements.push(
									<div key={`empty-${r}-${c}`} />
								);
								continue;
							}

							gridElements.push(
								<div
									key={field.fieldId}
									data-field-id={field.fieldId}
									className="min-w-0"
									style={
										(field.colSpan ?? 1) > 1
											? {
													gridColumn: `span ${field.colSpan}`,
												}
											: undefined
									}
								>
									<FieldRenderer
										fieldId={field.fieldId}
										required={field.required}
										inputHeight={ds.inputHeight}
									/>
								</div>
							);
						}
					}

					return (
						<div
							className="min-w-0 density-container"
							style={{
								padding: `${ds.padding}px`,
								fontSize: `${ds.fontSize}px`,
							}}
						>
							<div
								className="grid min-w-0"
								style={{
									gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
									gap: `${ds.gap}px`,
								}}
							>
								{gridElements}
							</div>
						</div>
					);
				}

				case "files":
					return (
						<JobFilesTab
							isExpanded={expandedFilesTabId === tab.id}
						/>
					);

				case "notes":
					return <JobNotesPanel />;

				case "job_history":
					return job?.patient_id ? (
						<PatientJobs
							patientId={job.patient_id}
							jobId={job.id ?? undefined}
							size="sm"
						/>
					) : null;

				case "deliveries":
					return <DeliveriesTab />;

				case "total":
					return <TotalTab />;

				default:
					return null;
			}
		},
		[
			job?.patient_id,
			job?.practitioner_id,
			job?.id,
			densitySettings,
			tabConfig,
			expandedFilesTabId,
		]
	);

	return (
		<div className="w-full h-full flex flex-col relative">
			<PatientDivergenceDialog />
			<InProgressJobsSuggestionDialog />
			{expandedFilesTabId && (
				<div className="absolute inset-0 z-20 bg-white flex flex-col">
					<div className="flex items-center justify-between px-3 py-1.5 border-b bg-gray-50 shrink-0">
						<span className="text-sm font-medium">Dateien</span>
						<div className="flex items-center gap-2">
							<div className="flex items-center gap-0.5 border rounded px-1 py-0.5">
								{(["small", "medium", "large"] as const).map(
									(s) => (
										<button
											key={s}
											className={cn(
												"h-5 px-1.5 text-[10px] rounded transition-colors",
												expandedPreviewSize === s
													? "bg-primary text-primary-foreground"
													: "hover:bg-gray-200"
											)}
											onClick={() =>
												setExpandedPreviewSize(s)
											}
										>
											{s === "small"
												? "S"
												: s === "medium"
													? "M"
													: "L"}
										</button>
									)
								)}
							</div>
							<FileTabActions
								isExpanded
								onToggleExpand={() =>
									setExpandedFilesTabId(null)
								}
							/>
						</div>
					</div>
					<div className="flex-1 overflow-auto">
						<JobFilesTab
							isExpanded
							previewSize={expandedPreviewSize}
						/>
					</div>
				</div>
			)}
			<div className="flex-1 flex min-h-0">
				<JobInfoTabsPanel
					tabConfig={tabConfig}
					closedTabs={closedTabs}
					isCollapsed={panelCollapsed}
					onToggleCollapsed={togglePanelCollapsed}
					onOpenTab={openTab}
					isFullscreen={isFullscreen}
					onToggleFullscreen={handleToggleFullscreen}
					getTabLabel={getTabLabel}
					tabHasIncompleteRequiredFields={
						tabHasIncompleteRequiredFields
					}
				/>

				{openTabs.length > 0 && (
					<div
						className={cn(
							"flex-1 min-w-0",
							isFullscreen
								? "flex flex-col"
								: "overflow-x-auto hide-scrollbar"
						)}
					>
						{isFullscreen ? (
							<>
								{[0, 1].map((rowIndex) => {
									const midpoint = Math.ceil(
										openTabs.length / 2
									);
									const rowTabs =
										rowIndex === 0
											? openTabs.slice(0, midpoint)
											: openTabs.slice(midpoint);
									if (rowTabs.length === 0) return null;

									return (
										<div
											key={rowIndex}
											className="flex-1 min-h-0 overflow-x-auto"
										>
											<div className="flex h-full">
												{rowTabs.map((tab) => {
													const textColorClass =
														getTextColorForBackground(
															tab.color
														);
													const incompleteFieldsInfo =
														tabHasIncompleteRequiredFields(
															tab
														);
													const fsCols =
														getTabCols(tab);
													const fsMinW =
														fsCols *
														TAB_WIDTH_PER_COL.min;
													return (
														<div
															key={tab.id}
															className="relative flex-shrink-0 h-full overflow-hidden"
															style={{
																flex: `1 0 ${fsMinW}px`,
																minWidth:
																	fsMinW,
															}}
															data-tab-panel-id={
																tab.id
															}
														>
															<TabPanelCard
																title={getTabLabel(
																	tab
																)}
																icon={
																	tab.icon
																		? renderIcon(
																				tab.icon,
																				{
																					className:
																						cn(
																							"h-4 w-4",
																							textColorClass
																						),
																				}
																			)
																		: undefined
																}
																headerButton={
																	tab.type ===
																	"files" ? (
																		<FileTabActions
																			isExpanded={
																				expandedFilesTabId ===
																				tab.id
																			}
																			onToggleExpand={() =>
																				handleToggleFilesExpand(
																					tab.id
																				)
																			}
																		/>
																	) : tab.type ===
																	  "deliveries" ? (
																		<CreateDeliveryButton />
																	) : undefined
																}
																onClose={() =>
																	closeTab(
																		tab.id
																	)
																}
																closeTestId={
																	tab.id ===
																	AD_HOC_TAB_ID
																		? "job-ad-hoc-tab-close"
																		: undefined
																}
																headerColor={
																	tab.color
																}
																headerTextColorClass={
																	textColorClass
																}
																incompleteFields={
																	incompleteFieldsInfo.hasIncomplete
																		? incompleteFieldsInfo.missingFields
																		: undefined
																}
																style={{
																	height: "100%",
																	backgroundColor:
																		tab.color
																			? `${tab.color}20`
																			: undefined,
																}}
															>
																{renderTabContent(
																	tab
																)}
															</TabPanelCard>
														</div>
													);
												})}
											</div>
										</div>
									);
								})}
							</>
						) : (
							<div className="flex h-full">
								{openTabs.map((tab, index) => {
									const textColorClass =
										getTextColorForBackground(tab.color);
									const width = getTabWidth(tab);
									const isLast =
										index === openTabs.length - 1;
									const incompleteFieldsInfo =
										tabHasIncompleteRequiredFields(tab);

									return (
										<div
											key={tab.id}
											className="relative flex-shrink-0 h-full overflow-hidden"
											style={{ width }}
											data-tab-panel-id={tab.id}
										>
											<TabPanelCard
												title={getTabLabel(tab)}
												compact={width < 180}
												icon={
													tab.icon
														? renderIcon(tab.icon, {
																className: cn(
																	"h-4 w-4",
																	textColorClass
																),
															})
														: undefined
												}
												headerButton={
													tab.type === "files" ? (
														<FileTabActions
															isExpanded={
																expandedFilesTabId ===
																tab.id
															}
															onToggleExpand={() =>
																handleToggleFilesExpand(
																	tab.id
																)
															}
														/>
													) : tab.type ===
													  "deliveries" ? (
														<CreateDeliveryButton />
													) : undefined
												}
												onClose={() => closeTab(tab.id)}
												closeTestId={
													tab.id === AD_HOC_TAB_ID
														? "job-ad-hoc-tab-close"
														: undefined
												}
												headerColor={tab.color}
												headerTextColorClass={
													textColorClass
												}
												incompleteFields={
													incompleteFieldsInfo.hasIncomplete
														? incompleteFieldsInfo.missingFields
														: undefined
												}
												style={{
													height: "100%",
													backgroundColor: tab.color
														? `${tab.color}20`
														: undefined,
												}}
											>
												{renderTabContent(tab)}
											</TabPanelCard>

											<div
												onMouseDown={(e) =>
													handleResizeStart(tab.id, e)
												}
												className={cn(
													"absolute top-0 right-0 w-1 h-full cursor-col-resize z-10",
													"hover:bg-blue-300 transition-colors",
													resizingTabId === tab.id &&
														"bg-blue-400"
												)}
												data-testid={`job-info-tab-resize-${tab.id}`}
												aria-label="Tab Größe ändern"
											/>
										</div>
									);
								})}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
